// The social pills in the roster shell's per-artist context header (ART-334/332): one per connected platform, its
// icon and follower count. The page registers them and the shell renders them — the `rosterHeaderAction.ts` shape,
// for the same reason: the shell does not read each lens's composed dashboard, the page does.
import { useEffect } from "react";
import { create } from "zustand";
import { registerStoreReset } from "./stores.js";

export interface RosterHeaderPill {
  platform: string;
  followers: number;
}

interface RosterHeaderPillsState {
  pills: readonly RosterHeaderPill[] | null;
  set: (pills: readonly RosterHeaderPill[]) => void;
  clear: () => void;
}

export const useRosterHeaderPillsStore = create<RosterHeaderPillsState>((set) => ({
  pills: null,
  set: (pills) => set({ pills }),
  clear: () => set({ pills: null }),
}));

// Scorch on org/band switch + logout: the previous artist's numbers must never sit over the next one.
registerStoreReset(() => useRosterHeaderPillsStore.getState().clear());

/**
 * Register this page's social pills for as long as it is mounted. Platforms with no count are dropped (never a
 * fabricated 0). Re-asserts across a scorch that empties the store without unmounting the page — the same artist
 * switch `useRosterHeaderAction` documents.
 */
export function useRosterHeaderPills(
  platforms: readonly { platform?: string | null; followers?: number | null }[] | null | undefined,
): void {
  const pills = (platforms ?? [])
    .filter((p): p is { platform: string; followers: number } => !!p.platform && p.followers != null)
    .map((p) => ({ platform: p.platform, followers: p.followers }));
  const key = JSON.stringify(pills);

  useEffect(() => {
    const value: RosterHeaderPill[] = JSON.parse(key);
    const store = useRosterHeaderPillsStore;
    store.getState().set(value);
    const unsubscribe = store.subscribe((s) => {
      if (s.pills === null) store.getState().set(value);
    });
    return () => {
      unsubscribe();
      store.getState().clear();
    };
  }, [key]);
}
